/**
 * Cost display helpers — formatting ONLY, never arithmetic.
 *
 * The backend computes every number and sends money as an exact string
 * (`"14.20000"`). This file turns those into something an admin can read. It
 * must never add, sum or multiply a cost: `0.1 + 0.2 !== 0.3` in JavaScript,
 * and a member's bill is not the place to discover that.
 *
 * One label map and one colour map live here on purpose. The same origin shows
 * up in a donut, a table, a tooltip and a drill-down; if each component mapped
 * it separately they would drift, and "Assistant replies" would be one colour
 * in the chart and another in the legend.
 *
 * Plan: docs/plans/SMS_COST_TRACKING_FRONTEND_PLAN.md §3.9
 */

/** Display order — roughly "member caused" first, then our own pushes. */
export const ORIGIN_ORDER = [
  'member_inbound',
  'assistant_reply',
  'proactive',
  'reminder',
  'broadcast',
  'admin_reply',
  'support_notify',
  'verification',
  'system_alert',
];

const ORIGIN_LABELS = {
  member_inbound: "Member's own messages",
  assistant_reply: 'Assistant replies',
  proactive: 'Proactive messages',
  reminder: 'Booking reminders',
  broadcast: 'Broadcasts',
  admin_reply: 'Admin replies',
  support_notify: 'Support notifications',
  verification: 'Verification codes',
  system_alert: 'System alerts',
};

const ORIGIN_COLORS = {
  member_inbound: '#2563eb',
  assistant_reply: '#0891b2',
  proactive: '#f59e0b',
  reminder: '#8b5cf6',
  broadcast: '#ec4899',
  admin_reply: '#10b981',
  support_notify: '#f97316',
  verification: '#6366f1',
  system_alert: '#64748b',
};

const UNKNOWN_COLOR = '#9ca3af';

const TRANSPORT_LABELS = {
  rcs: 'RCS (rich cards)',
  sms: 'SMS (plain text)',
  mms: 'MMS (picture)',
};

/**
 * Money string -> display currency.
 *
 * Sub-cent amounts keep 4 decimals: a single plain SMS costs about $0.0027, so
 * rounding to 2dp would render every individual message as "$0.00" and make the
 * per-message drill-down useless.
 *
 * Missing data renders as an em dash, never $0.00 — "not priced yet" and "cost
 * nothing" are different facts and must not look alike.
 */
export function formatCost(value) {
  if (value === null || value === undefined || value === '') return '—';

  const amount = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(amount)) return '—';

  if (amount !== 0 && Math.abs(amount) < 0.01) {
    return `$${amount.toFixed(4)}`;
  }
  return `$${amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Internal origin key -> admin-readable label. */
export function originLabel(origin) {
  if (!origin) return 'Not recorded';
  if (ORIGIN_LABELS[origin]) return ORIGIN_LABELS[origin];
  // An origin added on the backend before this map is updated should still read
  // as words, not as a raw key.
  const spaced = String(origin).replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Stable colour per origin, shared by every chart and legend. */
export function originColor(origin) {
  return ORIGIN_COLORS[origin] || UNKNOWN_COLOR;
}

/** Channel key -> label. `null` means the price sync hasn't reached it yet. */
export function transportLabel(transport) {
  if (!transport) return 'Not yet priced';
  return TRANSPORT_LABELS[transport] || String(transport).toUpperCase();
}
